<template>
  <TemplateBase>
    <div class="hidden"><!-- This health check is used by Exonet.  Do NOT remove it from the homepage! -->app-health-check:home</div>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Header -->
      <MoleculeHeaderHome
        class="relative"
        :class="{ 'header-margin': !page.campaign }"
        :title="page.title"
        :introduction="page.introduction"
        :image="page.image"
        :campaign="page.campaign"
      />

      <section v-if="page.highlights.length" id="homepage-highlight-articles" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <ul class="grid gap-y-4 grid-cols-1 mb-6 sm:gap-x-8 sm:gap-y-10 sm:grid-cols-2 lg:grid-cols-3 lg:mb-16">
          <li v-for="(item, index) in page.highlights" :key="index">
            <MoleculeCardHighlight :url="item.url" :title="item.title" :label="item.label" :image="item.image" :img-alt="item.title" />
          </li>
        </ul>
      </section>
      <section v-for="(item, index) of page.items" :key="index" class="article-overview-container relative z-10 mb-16 mx-auto md:container md:px-8 lg:mb-24">
        <MoleculeCollage id="homepage-article-overview" :label="item.label" :entries="item.children" class="article-overview-container__molecule">
          <template #large="{ entry }">
            <MoleculeCardOverviewCover
              :entry-type="get(entry, 'largeReference.label')"
              :entry-url="entry.largeReference && entry.largeReference.url"
              :entry-title="entry.label || (entry.largeReference && entry.largeReference.title)"
              :image="entry.largeImage || (entry.largeReference && entry.largeReference.image)"
              :collage-large="true"
            />
          </template>
          <template #medium="{ entry }">
            <MoleculeCardOverviewCover
              :entry-type="get(entry, 'mediumReference.label')"
              :entry-url="entry.mediumReference && entry.mediumReference.url"
              :entry-title="entry.label || (entry.mediumReference && entry.mediumReference.title)"
              :image="entry.mediumImage || (entry.mediumReference && entry.mediumReference.image)"
              :collage-medium="true"
            />
          </template>
        </MoleculeCollage>

        <div v-if="page.itemButtonTitle && page.itemButtonUrl" class="group flex mt-3">
          <AtomLink :url="page.itemButtonUrl" class="flex items-center ml-auto">
            <span class="text-black font-semibold border-b border-solid border-app-green">
              {{ page.itemButtonTitle }}
            </span>
            <AtomIconArrowRight class="ml-2 w-3 h-3 text-app-green transform group-hover:translate-x-1 transition duration-200" />
          </AtomLink>
        </div>
      </section>

      <section
        v-if="page.missionEmbed && page.missionTitle && page.missionDescription && page.missionItems"
        id="homepage-mission-article"
        class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8 md:mb-24"
      >
        <MoleculeMission
          :embed-title="page.homeMissionEmbedTitle"
          :embed="page.missionEmbed"
          :subtitle="page.missionTitle"
          :description="page.missionDescription"
          :items="page.missionItems"
        />
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'

interface PageProps {
  page: {
    title: string
    introduction: string
    image: string
    campaign?: {
      title: string
      introduction: string
      image: object
      primaryButtonLabel: string | null
      primaryButtonUrl: string | null
      secondaryButtonLabel: string | null
      secondaryButtonUrl: string | null
    }
    highlights: Array<{
      url: string
      title: string
      label: string
      image: string
    }>
    items: Array<{
      label: string
      children: Array<unknown>
    }>
    itemButtonTitle?: string
    itemButtonUrl?: string
    missionEmbed?: string
    missionTitle?: string
    missionDescription?: string
    missionItems?: Array<unknown>
    homeMissionEmbedTitle?: string
  }
}

defineProps<PageProps>()
</script>

<style lang="scss" scoped>
.header-margin {
  margin-bottom: calc(-1 * 100vw / 7);

  @screen sm {
    margin-bottom: calc(-1 * 100vw / 12);
  }

  @screen lg {
    margin-bottom: -10vw;
  }

  @screen xl {
    margin-bottom: -15vw;
  }

  @screen 2xl {
    margin-bottom: -17vw;
  }
}

.article-overview-container {
  @media screen and (min-width: calc(1280px + 96px)) {
    @apply px-0;

    .article-overview-container__molecule {
      @apply -mx-5;
    }
  }

  @screen 2xl {
    @apply px-56;

    .article-overview-container__molecule {
      @apply mx-0;
    }
  }

  @media screen and (min-width: calc(1750px + 96px)) {
    .article-overview-container__molecule {
      @apply -mx-12;
    }
  }
}
</style>
